import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import LanguageSwitcher from '@components/LanguageSwitcher';
import { BackTop, Collapse, Drawer } from 'antd';
import { isRtlLanguage } from '@i18n/config';

const { Panel } = Collapse;

// 创建图标封装组件
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  return null;
};

const Faq = () => {
  const { t, i18n } = useTranslation('pages');
  const { t: tCommon } = useTranslation('common');
  const [expandedItem, setExpandedItem] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRtl = isRtlLanguage(i18n.language);
  
  // 设置 HTML 的 dir 属性
  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
    return () => {
      document.documentElement.dir = 'ltr';
    };
  }, [isRtl, i18n.language]);

  const toggleAccordion = (index) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
          title={t('faq.seo.title')}
          description={t('faq.seo.description')}
          keywords={t('faq.seo.keywords')}
          canonicalUrl="https://snapbeautify.pixzens.com/faq"
      />
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt={t('about.logoText')} width="32" height="32" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                {tCommon('home')}
              </Link>
              <Link to="/quick-start" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                {tCommon('quickStart')}
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                {tCommon('about')}
              </Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title={tCommon('navigation')}
          placement={isRtl ? 'left' : 'right'}
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              {tCommon('home')}
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              {tCommon('quickStart')}
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              {tCommon('about')}
            </Link>
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <LanguageSwitcher />
            </div>
            <Link 
              to="/features" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Star" size={16} className="mr-1" />
              {tCommon('features')}
            </Link>
            <Link 
              to="/faq" 
              className="font-medium text-purple-600 dark:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="HelpCircle" size={16} className="mr-1" />
              {tCommon('faq')}
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2">
              <Link 
                to="/privacy" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Shield" size={16} className="mr-1" />
                {tCommon('privacy')}
              </Link>
              <Link 
                to="/terms" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="FileText" size={16} className="mr-1" />
                {tCommon('terms')}
              </Link>
              <Link 
                to="/copyright" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Award" size={16} className="mr-1" />
                {tCommon('copyright')}
              </Link>
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800 dark:text-white">{t('faq.title')}</h1>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
          {t('faq.description')}
        </p>
        
        <div className="max-w-4xl mx-auto grid gap-6">
          
          {/* 问题 1 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(0)}
            >
              <span>{t('faq.questions.q1.question')}</span>
              <SafeIcon
                icon={expandedItem === 0 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 0 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q1.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 2 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(1)}
            >
              <span>{t('faq.questions.q2.question')}</span>
              <SafeIcon
                icon={expandedItem === 1 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 1 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q2.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 3 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(2)}
            >
              <span>{t('faq.questions.q3.question')}</span>
              <SafeIcon
                icon={expandedItem === 2 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 2 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q3.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 4 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(3)}
            >
              <span>{t('faq.questions.q4.question')}</span>
              <SafeIcon
                icon={expandedItem === 3 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 3 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q4.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 5 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(4)}
            >
              <span>{t('faq.questions.q5.question')}</span>
              <SafeIcon
                icon={expandedItem === 4 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 4 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q5.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 6 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(5)}
            >
              <span>{t('faq.questions.q6.question')}</span>
              <SafeIcon
                icon={expandedItem === 5 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 5 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q6.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 7 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(6)}
            >
              <span>{t('faq.questions.q7.question')}</span>
              <SafeIcon
                icon={expandedItem === 6 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 6 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q7.answer')}
              </p>
            </div>
          </div>
          
          {/* 问题 8 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <button
              className="w-full px-6 py-4 text-left font-medium text-gray-800 dark:text-white flex justify-between items-center focus:outline-none"
              onClick={() => toggleAccordion(7)}
            >
              <span>{t('faq.questions.q8.question')}</span>
              <SafeIcon
                icon={expandedItem === 7 ? "ChevronUp" : "ChevronDown"}
                className="text-purple-500"
                size={24}
                useFa={true}
              />
            </button>
            <div
              className={`px-6 pt-0 overflow-hidden transition-all duration-300 ${
                expandedItem === 7 ? "max-h-96 pb-4" : "max-h-0"
              }`}
            >
              <p className="text-gray-600 dark:text-gray-400">
                {t('faq.questions.q8.answer')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 联系我们 */}
      <div className="container mx-auto px-4 py-8 mb-12">
        <div className="max-w-4xl mx-auto bg-purple-100 dark:bg-purple-900/30 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white flex items-center">
            <SafeIcon icon="MessageCircle" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
            {t('faq.moreQuestions.title')}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('faq.moreQuestions.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="mailto:support@pixzens.com" className="inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
              <SafeIcon icon="Mail" size={18} className="mr-2" useFa={true} />
              {t('faq.moreQuestions.sendEmail')}
            </a>
            <Link to="/quick-start" className="inline-flex items-center justify-center bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white px-4 py-2 rounded-lg transition-colors">
              <SafeIcon icon="BookOpen" size={18} className="mr-2" useFa={true} />
              {t('faq.moreQuestions.viewQuickStart')}
            </Link>
          </div>
        </div>
      </div>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <img src="/favicon.svg" alt={t('about.logoText')} width="24" height="24" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            
            <div className="flex space-x-8">
              <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('privacy')}</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('terms')}</Link>
              <Link to="/copyright" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('copyright')}</Link>
            </div>
            
            <div className="mt-4 md:mt-0 text-gray-400 text-sm">
              © {new Date().getFullYear()} {t('about.logoText')} | SnapBeautify
            </div>
          </div>
        </div>
      </footer>
      
      <BackTop>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg">
          <SafeIcon icon="ChevronUp" size={20} useFa={true} />
        </div>
      </BackTop>
    </div>
  );
};

export default Faq; 